import { RenderFieldExtensionCtx } from 'datocms-plugin-sdk'
import { useEffect, useRef } from 'react'
import { IPluginFieldType, IPluginState } from './types'

/**
 * Updates the JSON state of the plugin with the new data
 * @param ctx Context provided by DatoSDK
 * @param newState The new state
 * @param oldState The old state
 */
export function updatePluginField(
  ctx: RenderFieldExtensionCtx,
  newState: IPluginState,
  oldState: any,
  notAddedLanguages: string[],
  apiKey?: string
) {
  for (const language of Object.keys(newState)) {
    const newValue = notAddedLanguages.includes(language)
      ? null
      : JSON.stringify(newState[language], null, 2)
    // console.log('diff', oldState[language], newValue)
    if (newValue !== oldState[language]) {
      ctx.setFieldValue(
        (apiKey || ctx.field.attributes.api_key) + '.' + language,
        newValue
      )
    }
  }
}

/**
 * Reads the content of the plugin field
 * @param ctx Context object
 * @param field Current field of the plugin
 * @returns The parsed content or undefined
 */
export function parsePluginField(
  fieldValue?: IPluginFieldType
): IPluginState | undefined {
  if (!fieldValue || typeof fieldValue !== 'object') {
    return
  }

  return Object.keys(fieldValue).reduce((acc, fieldKey) => {
    let newValue = {}
    try {
      newValue = fieldValue[fieldKey] ? JSON.parse(fieldValue[fieldKey]) : {}
    } catch {
      //
    }
    acc[fieldKey] = newValue
    return acc
  }, {} as { [key: string]: any })
}

/**
 * Sets a field of a state by given language, field and value
 * @param state
 * @param language
 * @param field
 * @param value
 * @returns
 */
export function setFieldOfLanguageInState(
  state: IPluginState,
  language: string,
  field: string,
  value: any
) {
  if (!state) {
    return
  }
  if (!state[language]) {
    state[language] = {}
  }
  state[language][field] = value
}

export const isFieldValueEmpty = (fieldValue: any): boolean => {
  return (
    (!fieldValue && !(typeof fieldValue === 'boolean')) ||
    (Array.isArray(fieldValue) && fieldValue.length === 0) ||
    (Array.isArray(fieldValue) &&
      fieldValue.length === 1 &&
      fieldValue[0] instanceof Object &&
      'children' in fieldValue[0] &&
      !fieldValue[0].children?.[0]?.text &&
      !fieldValue[0].children?.[0]?.value &&
      !fieldValue[0].children?.[0]?.children) ||
    (fieldValue instanceof Object &&
      fieldValue.schema === 'dast' &&
      fieldValue.document?.children &&
      fieldValue.document?.children.length === 1 &&
      fieldValue.document?.children[0].children &&
      fieldValue.document?.children[0].children.length === 1 &&
      !fieldValue.document?.children[0].children[0].value &&
      !fieldValue.document?.children[0].children[0].children)
  )
}

export const getTranslatableFields = (
  fields: { [key: string]: any },
  excludedFields: any[],
  mainLanguage: string
) => {
  return Object.keys(fields).filter(
    (key: any) =>
      !excludedFields.includes(key) &&
      fields[key] instanceof Object &&
      Object.keys(fields[key]).includes(mainLanguage)
  )
}

export function usePrevious(value: any) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef()
  // Store current value in ref
  useEffect(() => {
    ref.current = value
  }, [value]) // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current
}
